import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useRewardState, { IReward } from '@/states/reward.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

export const useRewardLogic = () => {
  const formControl = useForm<IReward>({ defaultValues: { name: '', value: '', description: '' } });
  const { reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [allPublished, setAllPublished] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IReward | null>(null);
  const [sortedRewards, setSortedRewards] = useState<IReward[]>([]);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'org_chart_node.name',
    order: 'asc',
  });

  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();
  const { rewards, getRewards, createReward, updateReward, deleteReward, bulkPublishReward, isLoading } =
    useRewardState();

  const flattenTree = (nodes: any[]): any[] => {
    const result: any[] = [];
    nodes.forEach(node => {
      result.push(node);
      if (node.children?.length > 0) {
        result.push(...flattenTree(node.children));
      }
    });
    return result;
  };

  useEffect(() => {
    setAllPublished(rewards.length > 0 && rewards.every(item => item.is_published === true));
  }, [rewards]);

  useEffect(() => {
    if (!currentTenant?.id) return;
    const fetchData = async () => {
      try {
        const orgChartTree = await fetchOrgChartTree(currentTenant.id);
        if (orgChartTree) await getRewards(currentTenant.id);
      } catch {
        toast.error('Failed to fetch rewards');
      }
    };
    fetchData();
  }, [currentTenant?.id]);

  useEffect(() => {
    if (!rewards.length) return;
    const sorted = [...rewards].sort((a, b) => {
      const dir = pageConfig.order === 'asc' ? 1 : -1;
      if (pageConfig.orderBy === 'org_chart_node.name') {
        const aVal = a.org_chart_node?.name ?? '';
        const bVal = b.org_chart_node?.name ?? '';
        return aVal.localeCompare(bVal) * dir;
      }
      const key = pageConfig.orderBy as keyof IReward;
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
    setSortedRewards(sorted);
  }, [pageConfig.orderBy, pageConfig.order, rewards]);

  const filteredRewards = sortedRewards.filter(item => {
    const term = searchTerm.toLowerCase();
    const matchText =
      (item.name ?? '').toLowerCase().includes(term) ||
      (item.value ?? '').toLowerCase().includes(term) ||
      (item.description ?? '').toLowerCase().includes(term) ||
      (item.org_chart_node?.name ?? '').toLowerCase().includes(term);
    const matchRole = selectedRole ? String(item.org_chart_node_id) === selectedRole : true;
    const matchStatus = !selectedStatus || (selectedStatus === 'Published' ? item.is_published : !item.is_published);
    return matchText && matchRole && matchStatus;
  });

  const handleSave = async (data: IReward) => {
    if (!currentTenant?.id) return;
    try {
      const payload: Partial<IReward> = {
        org_chart_node_id: Number(data.org_chart_node_id),
        name: data.name,
        value: data.value,
        description: data.description,
        is_published: data.is_published ?? false,
        attachment_file: data.attachment_file,
      };

      if (formMode === 'edit' && selectedRow?.id) {
        await updateReward(currentTenant.id, selectedRow.id, payload);
      } else {
        await createReward(currentTenant.id, payload);
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({ name: '', value: '', description: '', org_chart_node_id: undefined });
      await getRewards(currentTenant.id);
    } catch {
      toast.error('Failed to save reward');
    }
  };

  const handleDelete = async (row: IReward) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteReward(currentTenant.id, row.id);
    await getRewards(currentTenant.id);
  };

  const allRoles = flattenTree(orgCharts);
  const roleOptions = allRoles.map(node => ({
    label: node.name,
    value: String(node.id),
  }));

  const fields = [
    {
      name: 'org_chart_node_id',
      label: 'Role',
      type: 'select',
      required: true,
      options: roleOptions,
      placeholder: 'Select role',
    },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter reward name',
    },
    {
      name: 'value',
      label: 'Reward',
      type: 'text',
      required: true,
      placeholder: 'Enter reward value',
      colSpan: 2,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'richtext',
      placeholder: 'Enter description',
      colSpan: 2,
    },
    {
      name: 'attachment_file',
      label: 'File Attachment',
      type: 'upload',
      uploadType: 'both' as const,
      colSpan: 2,
    },
  ];

  const bulkPublishRewardHandler = async (value: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishReward(currentTenant.id, value);
    await getRewards(currentTenant.id);
  };

  const onEdit = (row: IReward) => {
    let parsedAttachment = null;
    try {
      const files = row.attachment_file ? JSON.parse(row.attachment_file) : [];
      parsedAttachment = files.length > 0 ? files[0] : null;
    } catch {
      parsedAttachment = null;
    }

    reset({
      ...row,
      attachment_file: parsedAttachment
        ? [
          {
            name: parsedAttachment.original_name || parsedAttachment.path?.split('/').pop() || 'Unnamed File',
            url: parsedAttachment.url || parsedAttachment.path,
            mime_type: parsedAttachment.mime_type,
            size: parsedAttachment.size,
          },
        ]
        : [],
    });

    setFormMode('edit');
    setSelectedRow(row);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  return {
    isLoading,
    allPublished,
    filteredRewards,
    formControl,
    formRef,
    formMode,
    setFormMode,
    handleSave,
    handleDelete,
    reset,
    onEdit,
    pageConfig,
    setPageConfig,
    bulkPublishRewardHandler,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    clearFilters,
    orgCharts,
    fields,
    roleOptions,
  };
};
